/**
 * Identifies this browser to the backend's "restrict device access" check.
 *
 * The id is random, kept in localStorage, and shared by every app served from this origin (the admin
 * shell and the stock apps embedded in it). It travels as the `x-device-info` header — URI-encoded JSON —
 * on sign-in, OTP verification and when the restriction switch is turned on, so the backend can recognise
 * the device and approve the admin's own device. See main-backend helpers/adminDeviceAccess.helper.js.
 */

const DEVICE_ID_STORAGE_KEY = 'gi-device-id'

function randomId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_STORAGE_KEY)
    if (!id) {
      id = randomId()
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, id)
    }
    return id
  } catch {
    return null
  }
}

function describeUserAgent() {
  const ua = navigator.userAgent || ''
  let browser = null
  if (/Edg(e|A|iOS)?\//.test(ua)) browser = 'Edge'
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera'
  else if (/SamsungBrowser\//.test(ua)) browser = 'Samsung Internet'
  else if (/Firefox\/|FxiOS\//.test(ua)) browser = 'Firefox'
  else if (/Chrome\/|CriOS\//.test(ua)) browser = 'Chrome'
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) browser = 'Safari'

  // iPadOS reports itself as a Mac; touch support gives it away.
  const isIpad = /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  let os = null
  if (/iPhone|iPod/.test(ua) || isIpad) os = isIpad ? 'iPadOS' : 'iOS'
  else if (/Android/.test(ua)) os = 'Android'
  else if (/Windows NT/.test(ua)) os = 'Windows'
  else if (/CrOS/.test(ua)) os = 'ChromeOS'
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS'
  else if (/Linux/.test(ua)) os = 'Linux'

  let deviceType = 'desktop'
  if (isIpad || /Tablet/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) deviceType = 'tablet'
  else if (navigator.userAgentData?.mobile || /Mobi|iPhone|iPod|Android/.test(ua)) deviceType = 'mobile'

  const deviceName = browser && os ? `${browser} on ${os}` : os || browser || null
  return { browser, os, deviceType, deviceName }
}

/** Device fields for the `x-device-info` header. `coords` comes from {@link getDeviceCoordinates}. */
export function getDeviceInfo(coords) {
  return {
    deviceId: getDeviceId(),
    ...describeUserAgent(),
    ...(coords ? { latitude: coords.latitude, longitude: coords.longitude } : {}),
  }
}

export function deviceInfoHeaders(coords) {
  try {
    return { 'x-device-info': encodeURIComponent(JSON.stringify(getDeviceInfo(coords))) }
  } catch {
    return {}
  }
}

/**
 * The browser's position, or null. Without `prompt` it only answers when location permission was
 * already granted, so a normal sign-in never pops a permission dialog.
 */
export async function getDeviceCoordinates({ prompt = false, timeoutMs = 8000 } = {}) {
  if (!navigator.geolocation) return null
  try {
    if (!prompt && navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'geolocation' })
      if (status.state !== 'granted') return null
    }
  } catch {
    if (!prompt) return null
  }
  // The geolocation timeout only starts once permission is answered — race it so an ignored prompt can't hang.
  return Promise.race([
    new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 10 * 60 * 1000 }
      )
    }),
    new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs + 2000)),
  ])
}

/** Login responses that mean "this device is waiting for / was refused approval". */
export const DEVICE_ACCESS_CODES = ['device-pending', 'device-rejected']

export function isDeviceAccessResponse(response) {
  return response?.status === 423 && DEVICE_ACCESS_CODES.includes(response?.data?.code)
}
